import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import * as R from 'ramda';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Action, Provider, ProviderConfig, Resolver } from '../config';
import { EventPayload, EventType } from '../events';
import { MemoryRouterContext } from './MemoryRouterContext';
import WindowProvider from '../common/window';
import validationService, { TermsStepValue } from '../services/validation';
import { ExternalLinks } from '../config/defaultConfig';
import metadataCollector from '../services/metadata';
import { Routes } from '../config/routes';
import { useAuthentication } from './AuthenticationContext';
import { isNetworkSettable } from '../services/common/networkSettable';
import Log from '../common/logging';
import { CivicAnalyticsEvent } from '../services/analytics/civic/types';
import { FLOW_ID_HEADER } from '../common/constants';
import { GatekeeperNetworkServiceContext } from './GatekeeperNetworkService/GatekeeperNetworkServiceContext';
import { shouldAuthenticate } from '../config/configUtils';
import { MetadataContext } from './MetadataContext';
import { getStorageItemForWalletNetwork, setStorageItemForWalletNetwork, StorageKey } from '../utils/storage';
import { CredentialReuse } from '../common/types/credentials';

export type OptionalFlowExecutionConfig =
  | 'identityAuthId'
  | 'token'
  | 'wallet'
  | 'errorCode'
  | 'partnerAppId'
  | 'redirectUrl'
  | 'payload';

export interface FlowConfigProps {
  initialized: boolean;
  networkName: string;
  networkAddress: string;
  action: Action;
  chainName: string;
  chainNetwork: string;
  ownerSignsEnabled: boolean;
  domainName: string;
  provider?: Provider;
  acceptedDeclaration?: string;
  acceptedTermsAndConditionsLink?: string;
  acceptedPrivacyPolicyLink?: string;
  acceptedBiometricsLink?: string;
  providerExplainerAcknowledge: boolean;
  agreedToShareData: boolean;
  credentialExpired: boolean;
  feeAmountUSDCents?: number;
  selectProvider(selectedProvider?: Provider): void;
  navigateToNextFlow(): void;
  selectNetworkAddress(selectedNetworkAddress: string): void;
  getExternalLinksConfig(): ExternalLinks;
  setProviderExplainerAcknowledge(acknowledge: boolean): void;
  setAcceptedTermsAndConditionsLink(acceptedTermsAndConditionsLink: string): void;
  setAcceptedPrivacyPolicyLink(acceptedPrivacyPolicyLink: string): void;
  setAcceptedDeclaration(acceptedDeclaration: string): void;
  setAcceptedBiometricsLink(acceptedBiometricsLink?: string): void;
  getProviderConfig<T extends ProviderConfig>(provider: Provider): T;
  getOptionalFlowExecutionConfig(config: OptionalFlowExecutionConfig): string | null;
  processAcceptedTermsForUpload(): TermsStepValue;
  flowInProgress(selectedProvider: Provider): void;
  flowSuccessful(successPayload?: EventPayload): void;
  flowFailed(failedPayload?: EventPayload): void;
  flowCancelled(cancelledPayload?: EventPayload): void;
  setAgreedToShareData(agreed: boolean): void;
  userSignedTransactionComplete(successPayload?: EventPayload): void;
  validationStatusFetchComplete(payload?: EventPayload): void;
  civicAnalyticsEvent: CivicAnalyticsEvent | undefined;
  signedTx: string | undefined;
  hideWalletPrompts: boolean;
  flowType: string | undefined;
  showPIIShareAuthorization?: boolean;
  updateCivicAnalyticsEventData(civicAnalyticsEvent: CivicAnalyticsEvent): void;
  setCredentialReuse: (credentialReuse: CredentialReuse) => void;
  wallet: string | undefined;
  setFundsCheckPassed: (passed: boolean) => void;
}

export const FlowConfigContext = createContext<FlowConfigProps>({} as FlowConfigProps);

type ConfigProviderProps = {
  children: JSX.Element;
};

/**
 * Provider that adds the network, action and payload context
 * provides functionality to load the relevant configuration for an action provider
 * provider flow methods to handle flow successful, cancelled and failure
 * checks if wallet ownership should be displayed and orchestrates
 *
 * @param props
 * @constructor
 */
export function FlowConfigProvider(props: ConfigProviderProps): JSX.Element {
  const { queryParams } = useContext(MemoryRouterContext);
  const { isLoggedIn } = useAuthentication();
  const { gknServiceData } = useContext(GatekeeperNetworkServiceContext);
  const [userFlowType, setUserFlowType] = useState<string>('');
  const [networkAddress, setNetworkAddress] = useState<string>('');
  const [chainName, setChainName] = useState<string>('');
  const [chainNetworkName, setChainNetworkName] = useState<string>('');
  const [domainName, setDomainName] = useState<string>('');
  const [action, setAction] = useState<Action>(Action.ISSUANCE);
  const [provider, setProvider] = useState<Provider | undefined>(undefined);
  const [ownerSignsEnabled, setOwnerSignsEnabled] = useState<boolean>(true);
  const [hideWalletPrompts, setHideWalletPrompts] = useState<boolean>(false);
  const [credentialExpired, setCredentialExpired] = useState<boolean>(false);
  const [feeAmountUSDCents, setFeeAmountUSDCents] = useState<number | undefined>();
  const [initialized, setInitialized] = useState<boolean>(false);
  const [civicAnalyticsEvent, setCivicAnalyticsEvent] = useState<CivicAnalyticsEvent>();

  const { children } = props;
  const [acceptedTermsAndConditionsLink, setAcceptedTermsAndConditionsLink] = useState<string | undefined>(undefined);
  const [acceptedPrivacyPolicyLink, setAcceptedPrivacyPolicyLink] = useState<string | undefined>(undefined);
  const [acceptedDeclaration, setAcceptedDeclaration] = useState<string | undefined>(undefined);
  const [fundsCheckPassed, setFundsCheckPassed] = useState<boolean>(false);
  const [acceptedBiometricsLink, setAcceptedBiometricsLink] = useState<string | undefined>(undefined);
  const [providerExplainerAcknowledge, setProviderExplainerAcknowledge] = useState<boolean>(false);
  const [agreedToShareData, setAgreedToShareData] = useState<boolean>(false);
  const [remoteInstanceId, setRemoteInstanceId] = useState<string | undefined>(undefined);
  const [signedTxId, setSignedTxId] = useState<string | undefined>(undefined);
  const [credentialReuse, setCredentialReuse] = useState<CredentialReuse>();
  const navigate = useNavigate();

  const { metadata } = useContext(MetadataContext);

  const getCivicAnalyticsFromQueryParams = useCallback(() => {
    const {
      networkAddress: networkAddressParameter,
      action: actionParameter,
      chain,
      chainNetwork,
      domain,
      provider: providerParameter,
      ownerSigns: ownerSignsParameter,
      instanceId,
      did,
      referrer,
      redirectUrl,
      wallet,
      flowId,
      errorCode,
      partnerAppId,
      signedTx,
      flowType,
      hideWalletPrompts: hideWalletPromptsParameter,
      gatewayStatus, // The current status of the gateway-client-core. Helps when debugging blank errorCodes.
      civicClient, // The client version, either of the core or one of the framework-specific components.
      credentialExpired: credentialExpiredParameter,
      feeAmountUSDCents: feeAmountUSDCentsParameter,
    } = queryParams || {};

    const civicAnalytics = {
      networkAddress: networkAddressParameter,
      action: actionParameter,
      chain,
      chainNetwork,
      domain,
      provider: providerParameter,
      ownerSigns: ownerSignsParameter,
      instanceId,
      did,
      referrer,
      redirectUrl,
      wallet,
      flowId,
      errorCode,
      partnerAppId,
      signedTx,
      flowType,
      // the query parameter takes precedence over the GKN service data
      hideWalletPrompts:
        hideWalletPromptsParameter ||
        `${gknServiceData?.constraints?.public?.civicPassAuth?.hideWalletPrompts === true}`,
      validationId: metadata?.validationId,
      civicClient,
      gatewayStatus,
      credentialReuse: (credentialReuse?.credentialIds || [])?.length > 0,
      credentialReuseIds: credentialReuse?.credentialIds,
      credentialExpired: credentialExpiredParameter === 'true',
      feeAmountUSDCents: !R.isNil(feeAmountUSDCentsParameter) ? new Number(feeAmountUSDCentsParameter) : undefined,
    } as CivicAnalyticsEvent;
    return civicAnalytics;
  }, [queryParams, metadata?.validationId, JSON.stringify(gknServiceData), credentialReuse]);

  useEffect(() => {
    const civicAnalytics = getCivicAnalyticsFromQueryParams();
    setCivicAnalyticsEvent(civicAnalytics);
    setSignedTxId(civicAnalytics.signedTx as string);
    setRemoteInstanceId(civicAnalytics.instanceId);
    // If we don't have a clientflowType, like in the case of the revocation flow, skip the initialization
    if (gknServiceData?.clientFlowType) {
      const transformedNetworkName = Resolver.transformFlowType(gknServiceData!.clientFlowType);
      setUserFlowType(transformedNetworkName);
      if (isNetworkSettable(validationService)) {
        const flowAction =
          civicAnalytics.credentialExpired && civicAnalytics.action === Action.REFRESH
            ? Action.ISSUANCE
            : civicAnalytics.action;
        validationService.setNetworkContext(transformedNetworkName, flowAction as Action);
      }
    }
    setNetworkAddress(civicAnalytics.networkAddress);
    setChainName(civicAnalytics.chain || 'unknown');
    setChainNetworkName(civicAnalytics.chainNetwork);
    setAction(civicAnalytics.action as Action);
    // set to true if owner signs parameter is 'true', or default to true
    setOwnerSignsEnabled(civicAnalytics.ownerSigns === 'true' || !civicAnalytics.ownerSigns);
    if (civicAnalytics.provider && R.is(String, civicAnalytics.provider)) {
      const transformedProvider = Resolver.transformIncomingProvider(civicAnalytics.provider);
      setProvider(transformedProvider);
    }
    setDomainName(civicAnalytics.domain);
    setHideWalletPrompts(civicAnalytics.hideWalletPrompts === 'true');
    setCredentialExpired(civicAnalytics.credentialExpired!);
    setFeeAmountUSDCents(
      !R.isNil(civicAnalytics.feeAmountUSDCents) ? Number(civicAnalytics.feeAmountUSDCents) : undefined,
    );
    setInitialized(true);
  }, []);

  // We need to keep the action updated if the query param changes
  useEffect(() => {
    if (queryParams && queryParams.action) {
      setAction(queryParams.action as Action);
    }
    const civicAnalytics = getCivicAnalyticsFromQueryParams();
    setCivicAnalyticsEvent(civicAnalytics);
    axios.defaults.headers.common[FLOW_ID_HEADER] = queryParams.flowId;
  }, [queryParams, getCivicAnalyticsFromQueryParams]);

  /** Return the value of an optional query string parameter for the flow or null */
  function getOptionalFlowExecutionConfig(config: OptionalFlowExecutionConfig): string | null {
    const configValue = R.propOr(null, config, queryParams || {});

    if (!R.isNil(configValue) && R.is(String, configValue)) {
      return configValue as string;
    }

    return null;
  }

  /**
   * Update analytics data that needs to be refreshed or updated
   * @param civicAnalyticsEvent
   */
  const updateCivicAnalyticsEventData = (inCivicAnalyticsEvent: CivicAnalyticsEvent) => {
    setCivicAnalyticsEvent(inCivicAnalyticsEvent);
  };

  useEffect(() => {
    if (R.isEmpty(networkAddress)) {
      return;
    }

    const wallet = getOptionalFlowExecutionConfig('wallet');
    if (!R.isEmpty(networkAddress)) {
      metadataCollector.updateMetadata({
        networkAddress,
        domain: domainName,
        ...(wallet ? { wallet } : {}),
      });
    }
    setAcceptedBiometricsLink(
      getStorageItemForWalletNetwork(StorageKey.AcceptedBiometrics, networkAddress, wallet) || undefined,
    );
    setAcceptedTermsAndConditionsLink(
      getStorageItemForWalletNetwork(StorageKey.AcceptedTerms, networkAddress, wallet) || undefined,
    );
    setAcceptedPrivacyPolicyLink(
      getStorageItemForWalletNetwork(StorageKey.AcceptedPrivacyPolicy, networkAddress, wallet) || undefined,
    );
  }, [networkAddress]);

  function selectProvider(selectedProvider?: Provider): void {
    setProvider(selectedProvider);
  }

  function selectNetworkAddress(selectedNetworkAddress: string): void {
    const transformedUserflowType = Resolver.transformFlowType(gknServiceData!.clientFlowType);
    setUserFlowType(transformedUserflowType);
    setNetworkAddress(selectedNetworkAddress);
    if (isNetworkSettable(validationService)) {
      const flowAction = credentialExpired && action === Action.REFRESH ? Action.ISSUANCE : action;
      validationService.setNetworkContext(transformedUserflowType, flowAction as Action);
    }
  }

  function getProviderConfig<T extends ProviderConfig>(forProvider: Provider): T {
    const flowAction = credentialExpired && action === Action.REFRESH ? Action.ISSUANCE : action;
    return Resolver.getProviderConfig<T>(gknServiceData!.clientFlowType, flowAction, forProvider, chainName);
  }

  function selectedProviderConfigOrDefault(): ProviderConfig {
    Log.debug('[FlowConfigContext] selectedProviderConfigOrDefault', provider);
    if (provider) {
      return getProviderConfig(provider);
    }

    const flowAction = credentialExpired && action === Action.REFRESH ? Action.ISSUANCE : action;
    const providerConfigs = Resolver.getProviderConfigurations(gknServiceData!.clientFlowType, flowAction);
    Log.debug('[FlowConfigContext] providerConfigs', providerConfigs);
    // If there are no provider configs, then return the default route with show self declaration as true
    if (R.isEmpty(providerConfigs)) {
      return {
        route: Resolver.getRoute(gknServiceData!.clientFlowType, flowAction) as Routes,
        showSelfDeclaration: false,
      };
    }

    // Otherwise return the config for the first provider if no provider was explicitly selected
    return providerConfigs[R.keys(providerConfigs)[0]] as ProviderConfig;
  }

  const showPIIShareAuthorization = R.pathOr(
    false,
    ['constraints', 'public', 'kyc', 'piiSharingEnabled'],
    gknServiceData,
  );

  function navigateToNextFlow() {
    const providerConfig = selectedProviderConfigOrDefault();
    if (providerConfig.providerExplainerRoute && !providerExplainerAcknowledge) {
      return navigate(providerConfig.providerExplainerRoute);
    }

    if (showPIIShareAuthorization && !agreedToShareData) {
      return navigate(Routes.CAPTURE_PERSONAL_DETAILS);
    }

    // if the user isn't logged in, and they should authenticate, navigate to the login screen
    if (shouldAuthenticate(ownerSignsEnabled, providerConfig.authenticationDisabled) && !isLoggedIn()) {
      return navigate(Routes.LOGIN);
    }

    if (providerConfig.showSelfDeclaration && !acceptedDeclaration) {
      return navigate(Routes.DECLARATIONS);
    }

    if (ownerSignsEnabled && !fundsCheckPassed) {
      return navigate(Routes.FUNDS_CHECK);
    }
    return navigate(providerConfig.route);
  }

  function getExternalLinksConfig(): ExternalLinks {
    return Resolver.getExternalLinksConfig();
  }

  function flowInProgress(selectedProvider: Provider): void {
    WindowProvider.emit({
      action,
      event: EventType.IN_PROGRESS,
      payload: {
        provider: selectedProvider,
      },
      instanceId: remoteInstanceId,
    });
  }

  /** Merge accepted terms, declaration and biometrics into payload if avaiable */
  function mergeAcceptedTerms(successPayload?: EventPayload): EventPayload | undefined {
    let payload = successPayload;

    if (acceptedTermsAndConditionsLink) {
      payload = R.merge({ acceptedTermsAndConditionsLink }, payload || {});
    }

    if (acceptedPrivacyPolicyLink) {
      payload = R.merge({ acceptedPrivacyPolicyLink }, payload || {});
    }

    if (acceptedDeclaration) {
      payload = R.merge({ acceptedDeclaration }, payload || {});
    }

    if (acceptedBiometricsLink) {
      payload = R.merge({ acceptedBiometricsPolicyLink: acceptedBiometricsLink }, payload || {});
    }

    return payload;
  }

  function userSignedTransactionComplete(payload?: EventPayload): void {
    const flowAction = credentialExpired && action === Action.REFRESH ? Action.ISSUANCE : action;

    const requiresProofOfWalletOwnership = Resolver.flowRequiresLegacyPOWO(
      gknServiceData!.clientFlowType,
      flowAction as Action,
      chainName,
    );
    Log.debug(`flowSuccessful requiresProofOfWalletOwnership: ${!!requiresProofOfWalletOwnership}`);
    WindowProvider.emit({
      action,
      event: EventType.SUCCESS,
      payload,
      requiresProofOfWalletOwnership: !!requiresProofOfWalletOwnership,
      instanceId: remoteInstanceId,
    });
  }

  function validationStatusFetchComplete(payload?: EventPayload): void {
    WindowProvider.emit({
      action: Action.STATUS,
      event: EventType.SUCCESS,
      payload,
      instanceId: remoteInstanceId,
    });
  }

  function flowSuccessful(payload?: EventPayload): void {
    const mergedPayload = payload ? mergeAcceptedTerms(payload) : payload;
    const requiresUserSignature = ownerSignsEnabled && [Action.ISSUANCE, Action.REFRESH].includes(action);
    if (requiresUserSignature) {
      navigate(Routes.SIGN_TRANSACTION, { state: mergedPayload });
      return;
    }

    userSignedTransactionComplete(mergedPayload);
  }

  function flowFailed(failedPayload?: EventPayload): void {
    WindowProvider.emit({
      action,
      event: EventType.FAILURE,
      payload: failedPayload,
      instanceId: remoteInstanceId,
    });
  }

  function flowCancelled(cancelledPayload?: EventPayload): void {
    WindowProvider.emit({
      action,
      event: EventType.CANCELLED,
      payload: cancelledPayload,
      instanceId: remoteInstanceId,
    });
  }

  /**
   * Extract the accepted terms & declaration links for upload
   * clears accepted terms as they should no longer be emitted on success
   * @returns TermsStepValue
   */
  function processAcceptedTermsForUpload(): TermsStepValue {
    const acceptedTerms: TermsStepValue = {
      acceptedTermsAndConditionsLink,
      acceptedDeclaration,
      acceptedBiometricsPolicyLink: acceptedBiometricsLink,
    };

    /* TODO CIV-4196 Pass through terms on success event until gatekeeper is updated
    setAcceptedTermsAndConditionsLink(undefined);
    setAcceptedBiometricsLink(undefined);
    setAcceptedDeclaration(undefined);
    */
    return acceptedTerms;
  }

  const setAcceptedTermsAndConditionsLinkAndUpdateStore = useCallback(
    (link: string) => {
      const wallet = getOptionalFlowExecutionConfig('wallet');
      setStorageItemForWalletNetwork(StorageKey.AcceptedTerms, networkAddress, wallet, link);
      setAcceptedTermsAndConditionsLink(link);
    },
    [networkAddress],
  );

  const setAcceptedPrivacyPolicyLinkAndUpdateStore = useCallback(
    (link: string) => {
      const wallet = getOptionalFlowExecutionConfig('wallet');
      setStorageItemForWalletNetwork(StorageKey.AcceptedPrivacyPolicy, networkAddress, wallet, link);
      setAcceptedPrivacyPolicyLink(link);
    },
    [networkAddress],
  );

  const setAcceptedBiometricPolicyLinkAndUpdateStore = useCallback(
    (link: string) => {
      const wallet = getOptionalFlowExecutionConfig('wallet');
      setStorageItemForWalletNetwork(StorageKey.AcceptedBiometrics, networkAddress, wallet, link);
      setAcceptedBiometricsLink(link);
    },
    [networkAddress],
  );
  const context = useMemo(
    () => ({
      initialized,
      networkName: userFlowType,
      networkAddress,
      action,
      chainName,
      chainNetwork: chainNetworkName,
      domainName,
      provider,
      ownerSignsEnabled,
      acceptedDeclaration,
      acceptedTermsAndConditionsLink,
      acceptedPrivacyPolicyLink,
      acceptedBiometricsLink,
      providerExplainerAcknowledge,
      agreedToShareData,
      showPIIShareAuthorization,
      navigateToNextFlow,
      getOptionalFlowExecutionConfig,
      selectProvider,
      setProviderExplainerAcknowledge,
      selectNetworkAddress,
      setAcceptedTermsAndConditionsLink: setAcceptedTermsAndConditionsLinkAndUpdateStore,
      setAcceptedPrivacyPolicyLink: setAcceptedPrivacyPolicyLinkAndUpdateStore,
      setAcceptedBiometricsLink: setAcceptedBiometricPolicyLinkAndUpdateStore,
      setAcceptedDeclaration,
      getProviderConfig,
      processAcceptedTermsForUpload,
      flowInProgress,
      flowSuccessful,
      flowFailed,
      flowCancelled,
      getExternalLinksConfig,
      setAgreedToShareData,
      userSignedTransactionComplete,
      validationStatusFetchComplete,
      civicAnalyticsEvent,
      signedTx: signedTxId,
      hideWalletPrompts,
      flowType: civicAnalyticsEvent?.flowType,
      updateCivicAnalyticsEventData,
      setCredentialReuse,
      feeAmountUSDCents,
      credentialExpired,
      wallet: civicAnalyticsEvent?.wallet,
      setFundsCheckPassed,
    }),
    [
      initialized,
      userFlowType,
      networkAddress,
      action,
      chainName,
      chainNetworkName,
      domainName,
      provider,
      ownerSignsEnabled,
      acceptedDeclaration,
      acceptedTermsAndConditionsLink,
      acceptedPrivacyPolicyLink,
      acceptedBiometricsLink,
      providerExplainerAcknowledge,
      navigateToNextFlow,
      getOptionalFlowExecutionConfig,
      selectProvider,
      setProviderExplainerAcknowledge,
      selectNetworkAddress,
      setAcceptedDeclaration,
      getProviderConfig,
      processAcceptedTermsForUpload,
      flowInProgress,
      flowSuccessful,
      flowFailed,
      flowCancelled,
      getExternalLinksConfig,
      setAgreedToShareData,
      agreedToShareData,
      userSignedTransactionComplete,
      validationStatusFetchComplete,
      civicAnalyticsEvent,
      signedTxId,
      hideWalletPrompts,
      showPIIShareAuthorization,
      updateCivicAnalyticsEventData,
      setAcceptedTermsAndConditionsLinkAndUpdateStore,
      setAcceptedPrivacyPolicyLinkAndUpdateStore,
      setAcceptedBiometricPolicyLinkAndUpdateStore,
      setCredentialReuse,
      credentialExpired,
      feeAmountUSDCents,
      setFundsCheckPassed,
    ],
  );

  return <FlowConfigContext.Provider value={context}>{children}</FlowConfigContext.Provider>;
}
export const useFlowConfig = (): FlowConfigProps => useContext(FlowConfigContext);
